import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import uuid from 'uuid/v4';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';

import { BackendExperimentContext } from '@atlassian/search-experiment/backend-experiment-context';

import { DocumentContentTitle } from '@confluence/document-title/entry-points/DocumentContentTitle.ts';
import { fg } from '@confluence/feature-gating';
// eslint-disable-next-line no-restricted-imports -- deprecated - use RouteState instead
import { RoutesContext } from '@confluence/route-manager/entry-points/RoutesContext.ts';
import { RovoConfigProvider } from '@confluence/rovo-config-provider-common/entry-points/RovoConfigProvider.ts';
import { RegisteredExperience } from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { i18n } from './AdvancedSearch-i18n.ts';
import { AdvancedSearchRovoConfigProvider } from './AdvancedSearchRovoConfigProvider.tsx';
import { AdvancedSearchLayout } from './AdvancedSearchLayout.tsx';
import { AnalyticsProvider } from './AnalyticsProvider.tsx';
import { SearchInputProvider } from './SearchInputProvider.tsx';
import { SearchPage } from './SearchPageAsync.tsx';
import { SearchPageLoadUFOExperienceStart } from './SearchPageLoadStart.ts';
import { SearchResultsProvider } from './SearchResultsProvider.tsx';

export const AdvancedSearchController = (): React.JSX.Element => {
	const intl = useIntl();
	const { isRovoEnabled: isRovoEnabledInTCS } = useSessionData();

	// A user sees Full Page Search from `@atlassian/search-page` if:
	// - They have Rovo or the FG for Unified Search beta (in development)
	// For now, the Rovo Opt Out also overrides the Unified Search FG

	const isFullPageSearchEnabled =
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		(fg('unified_search_in_confluence') || isRovoEnabledInTCS) &&
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		!fg('confluence_rovo_search_opt_out');

	const [shouldShowSearchPageRovoConfig, setShouldShowSearchPageRovoConfig] = useState(false);
	const [showFSJNudge, setShowFSJNudge] = useState(false);

	const { getQueryParams, setQueryParams } = useContext(RoutesContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const queryParams = getQueryParams();
	const currentQuery = queryParams?.text?.toString() ?? '';
	const searchReferrerId = useRef(queryParams.search_id?.toString()).current;
	const searchSessionId = useRef(uuid()).current;
	const sendSessionStartedEvent = useCallback(() => {
		const analyticsEvent = createAnalyticsEvent({
			type: 'sendOperationalEvent',
			data: {
				source: 'advancedSearchScreen',
				action: 'started',
				actionSubject: 'advancedSearchSession',
				attributes: {
					searchSessionId,
					searchReferrerId,
					version: 2,
				},
			},
		});
		analyticsEvent.fire();
	}, [createAnalyticsEvent, searchSessionId, searchReferrerId]);

	const changeSearchIdInQueryParams = useCallback(() => {
		setQueryParams(
			{
				search_id: searchSessionId,
			},
			true,
		);
	}, [setQueryParams, searchSessionId]);

	const handleRedirect = useCallback(() => {
		const { redirect, unsupportedFields, doubleFields } = getQueryParams();
		if (redirect) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'redirected',
					actionSubject: 'advancedSearch',
					source: 'advancedSearchScreen',
					attributes: {
						unsupportedFields,
						doubleFields,
					},
				},
			}).fire();
			setQueryParams({
				redirect: null,
			});
		}
		if (unsupportedFields) {
			setQueryParams({
				unsupportedFields: null,
			});
		}
		if (doubleFields) {
			setQueryParams({
				doubleFields: null,
			});
		}
	}, [createAnalyticsEvent, setQueryParams, getQueryParams]);

	useEffect(() => {
		sendSessionStartedEvent();
		changeSearchIdInQueryParams();
		handleRedirect();
	}, [sendSessionStartedEvent, changeSearchIdInQueryParams, handleRedirect]);

	useEffect(() => {
		// Failed Search Journey config and nudge should not be enabled if the user is not on FPS OR their query is empty
		if (!isFullPageSearchEnabled || currentQuery.length === 0) {
			// Reset both flags so nudge doesn't persist after query is cleared or FPS is disabled
			setShouldShowSearchPageRovoConfig(false);
			setShowFSJNudge(false);
			return;
		}

		//Show the FSJ Config
		setShouldShowSearchPageRovoConfig(true);
		// Reset nudge so the 20s timer restarts fresh on each new query
		setShowFSJNudge(false);

		// Tenants/cloud IDs that have opted out should never see the FSJ nudge
		// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
		if (fg('fsj_nudge_opt_out')) {
			return;
		}

		//Set the FSJ nudge to show after 20s dwell time
		const timeoutId = setTimeout(() => {
			setShowFSJNudge(true);
		}, 20000);
		return () => clearTimeout(timeoutId);
	}, [isFullPageSearchEnabled, currentQuery]);

	return (
		<UFOSegment name="advanced-search-controller">
			{isFullPageSearchEnabled ? (
				<>
					<RovoConfigProvider
						experience={RegisteredExperience.SEARCH_PAGE}
						provider={AdvancedSearchRovoConfigProvider}
						getSearchQuery={() => getQueryParams()?.text?.toString()}
						shouldUseSearchPageRovoConfig={shouldShowSearchPageRovoConfig}
						shouldShowFSJNudge={showFSJNudge}
					/>
					<SearchPageLoadUFOExperienceStart />
					<SearchPage />
					<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.header)} />
				</>
			) : (
				<SearchInputProvider>
					<DocumentContentTitle titlePrefix={intl.formatMessage(i18n.header)} />
					<BackendExperimentContext>
						<SearchResultsProvider searchSessionId={searchSessionId}>
							<AnalyticsProvider
								searchSessionId={searchSessionId}
								createAnalyticsEvent={createAnalyticsEvent}
							>
								<AdvancedSearchLayout />
							</AnalyticsProvider>
						</SearchResultsProvider>
					</BackendExperimentContext>
				</SearchInputProvider>
			)}
		</UFOSegment>
	);
};
